import { http } from "../lib/http";

/* =========================================================================
   SUPERFICIE DE API DEL PANEL DE ALIADOS

   Un único lugar donde vive cada llamada. Las pantallas no arman URLs: si una
   ruta cambia en Laravel, se corrige acá y nada más.

   HAY DOS FAMILIAS, una por ámbito: `/conjunto/*` para el edificio y
   `/negocio/*` para la tienda. Ninguna llamada de ninguna de las dos lleva el
   identificador de a quién pertenece lo que pide.

   No es un descuido: el servidor lo resuelve desde la sesión. Si el conjunto
   viajara en la URL, cambiar un número dejaría ver la portería del edificio
   del vecino; con el negocio sería peor todavía, porque los pedidos traen
   nombre, teléfono y dirección de cada comprador.

   El tendero con dos locales elige cuál administra, y esa elección viaja en la
   cabecera `X-Negocio` que pone el cliente HTTP una sola vez —ver
   `lib/http.js`—, no en cada llamada de acá.
   ======================================================================== */

export const auth = {
  login: (email, password) =>
    http.post("/login", { email, password }).then((r) => r.data),
  /* Se usa al recargar: el token guardado puede seguir siendo válido, y hay
     que saber quién es antes de pintar nada. */
  profile: () => http.get("/profile").then((r) => r.data),
  logout: () => http.post("/logout").then((r) => r.data),
};

/** Lo que el panel necesita al entrar: su conjunto, su rol y sus permisos. */
export const miConjunto = {
  cargar: () => http.get("/conjunto/me").then((r) => r.data),
};

export const porteria = {
  /** Con `code` (el QR del domiciliario) o con `document` (su cédula). */
  verificar: (body) =>
    http.post("/conjunto/porteria/verificar", body).then((r) => r.data),
  /** `params`: search, method, kind, desde, hasta, dentro. Todos opcionales. */
  entradas: (params) =>
    http.get("/conjunto/porteria/entradas", { params }).then((r) => r.data),

  /**
   * Registrar a alguien que NO es usuario de la plataforma: una visita,
   * personal de servicio, un domicilio de otra app.
   *
   * No verifica nada: anota lo que el celador ve. Quien decide si pasa es él,
   * con el citófono en la mano.
   */
  visitante: (body) =>
    http.post("/conjunto/porteria/visitantes", body).then((r) => r.data),

  /** Marcar la salida. Sirve igual para domiciliarios y para visitantes. */
  salida: (id) =>
    http.put(`/conjunto/porteria/entradas/${id}/salida`).then((r) => r.data),

  /** Quién entró hoy y todavía no ha salido. */
  adentro: () => http.get("/conjunto/porteria/adentro").then((r) => r.data),
};

/** La ficha del conjunto: sus datos, su foto y sus reglas de portería. */
export const miConjuntoPerfil = {
  actualizar: (body) => http.put("/conjunto/perfil", body).then((r) => r.data),
  foto: (archivo) => {
    const fd = new FormData();
    fd.append("file", archivo);
    // Sin Content-Type a mano: el navegador tiene que poner el boundary, y
    // forzarlo rompe la subida. De eso se encarga el interceptor.
    return http.post("/conjunto/perfil/foto", fd).then((r) => r.data);
  },
};

/** El tablero del conjunto. Lo ven el administrador y el celador. */
export const resumenConjunto = {
  cargar: (dias) =>
    http.get("/conjunto/resumen", { params: { dias } }).then((r) => r.data),
};

/** El histórico del edificio. Sólo el administrador. */
export const reportesConjunto = {
  cargar: (params) =>
    http.get("/conjunto/reportes", { params }).then((r) => r.data),
  /**
   * El Excel se pide como binario y se entrega al navegador desde memoria.
   *
   * No se puede abrir la URL en una pestaña: la sesión va en la cabecera
   * `Authorization`, no en una cookie, así que una pestaña nueva llegaría sin
   * token y descargaría un 401 en un archivo .xlsx.
   */
  excel: (params) =>
    http
      .get("/conjunto/reportes/excel", { params, responseType: "blob" })
      .then((r) => r.data),
};

/** Cuánta gente del conjunto usa la plataforma, por torre.

    Sin correos ni teléfonos: son datos personales de terceros y su relación es
    con la plataforma, no con la administración del edificio. */
export const residentes = {
  /** Cuántos hay por torre. La cifra, no las personas. */
  list: () => http.get("/conjunto/residentes").then((r) => r.data),
  /**
   * Quiénes son: nombre, torre y apartamento, paginado desde el servidor.
   *
   * Sin correo ni teléfono a propósito. Una administración lleva
   * legítimamente el registro de quién vive en su edificio; los datos de
   * CONTACTO de cada vecino son de su relación con la plataforma.
   */
  detalle: (params) =>
    http.get("/conjunto/residentes/detalle", { params }).then((r) => r.data),
};

export const celadores = {
  list: () => http.get("/conjunto/celadores").then((r) => r.data),
  create: (body) => http.post("/conjunto/celadores", body).then((r) => r.data),
  update: (id, body) =>
    http.put(`/conjunto/celadores/${id}`, body).then((r) => r.data),
};

/* ======================== NEGOCIO (el tendero) ======================== */

/** Su local, la lista de los que administra, sus permisos y las cifras del
    encabezado. Es el equivalente de `/conjunto/me`. */
export const miNegocio = {
  cargar: () => http.get("/negocio/me").then((r) => r.data),
  actualizar: (body) => http.put("/negocio/me", body).then((r) => r.data),
};

export const pedidos = {
  list: () => http.get("/negocio/pedidos").then((r) => r.data),
  /**
   * Mover un pedido de estado.
   *
   * `user_id` es el del DOMICILIARIO y solo hace falta al despachar (2 → 3).
   * No es el identificador del domiciliario sino el de su usuario: son dos
   * números distintos y el servidor espera el segundo.
   */
  mover: ({ order_id, state, user_id }) =>
    http
      .put("/negocio/pedidos/estado", { order_id, state, user_id })
      .then((r) => r.data),

  /**
   * Registrar cuánto pesó lo que se vende al peso.
   *
   * NO cambia el total: el cliente pidió «3.000 de queso» o «500 g de
   * tomate» y eso es lo que paga. Esto es para el inventario y para dejar
   * constancia de quién pesó qué.
   *
   * Va por `orderDet_id` y no por producto: un pedido puede traer la misma
   * referencia en dos líneas y con el producto no se sabe a cuál va cada peso.
   */
  pesar: ({ order_id, lineas }) =>
    http.put("/negocio/pedidos/pesaje", { order_id, lineas }).then((r) => r.data),

  /**
   * El comprobante de un pedido entregado.
   *
   * Solo existe desde que se entrega: antes, el pedido todavia puede
   * cancelarse y un comprobante de algo que no ocurrio habria que anularlo.
   * Si el pedido se entrego sin el --uno viejo, o una emision que se cayo--,
   * pedirlo lo emite; el servidor no crea dos.
   *
   * `pdf_url` viene firmada y caduca a la media hora: es un enlace que abre
   * un navegador, y un navegador no manda el token de la sesion.
   */
  comprobante: (id) =>
    http.get(`/negocio/pedidos/${id}/comprobante`).then((r) => r.data),

  /**
   * Cancelar un pedido que la tienda no puede preparar.
   *
   * El motivo es OBLIGATORIO acá y no lo es cuando cancela el comprador: el
   * cliente no hizo nada y su pedido desaparece, así que es lo único que le
   * explica lo que pasó. Se le manda tal cual en el aviso.
   *
   * Hasta el estado 2. Después hay alguien en la calle con la bolsa encima y
   * el servidor responde 422.
   */
  cancelar: ({ order_id, motivo }) =>
    http
      .put(`/negocio/pedidos/${order_id}/cancelar`, { motivo })
      .then((r) => r.data),
};

export const ingresos = {
  cargar: () => http.get("/negocio/ingresos").then((r) => r.data),
};

export const productos = {
  list: () => http.get("/negocio/productos").then((r) => r.data),
  esquema: () => http.get("/negocio/productos/esquema").then((r) => r.data),

  /*
   * AGREGAR ES ELEGIR DEL CATÁLOGO, no escribir un producto.
   *
   * El cuerpo son tres datos y ninguno es texto: cuál producto del catálogo
   * maestro, a cuánto lo vende esta tienda y cuántos tiene. Antes esto mandaba
   * nombre y categoría y el servidor creaba una fila en `products` —el
   * catálogo de TODA la plataforma— con lo que el tendero escribiera.
   */
  agregar: (body) => http.post("/negocio/productos", body).then((r) => r.data),

  update: (id, body) =>
    http.put(`/negocio/productos/${id}`, body).then((r) => r.data),

  quitar: (id) => http.delete(`/negocio/productos/${id}`).then((r) => r.data),

  /** Precios y existencias de varios a la vez, en una transacción. */
  precios: (cambios) =>
    http.put("/negocio/productos/precios", { cambios }).then((r) => r.data),

  tiendasParaCopiar: () =>
    http.get("/negocio/productos/tiendas-para-copiar").then((r) => r.data),

  copiar: (desde) =>
    http.post("/negocio/productos/copiar", { desde }).then((r) => r.data),
};

/** El catálogo maestro, de donde el tendero elige. */
export const catalogo = {
  buscar: (q) =>
    http.get("/negocio/catalogo", { params: { q, limit: 25 } }).then((r) => r.data),

  /*
   * Un 404 acá NO es un error: es el caso normal de un producto que nadie ha
   * cargado, y la pantalla lo usa para ofrecer darlo de alta. Por eso devuelve
   * null en vez de dejar que reviente.
   */
  porCodigo: (barcode) =>
    http
      .get(`/negocio/catalogo/codigo/${encodeURIComponent(barcode)}`)
      .then((r) => r.data?.data ?? null)
      .catch((e) => {
        if (e?.response?.status === 404) return null;
        throw e;
      }),

  proponer: (body) =>
    http.post("/negocio/catalogo/proponer", body).then((r) => r.data),
};

/** Cargar el catálogo desde un Excel. */
export const cargasDeCatalogo = {
  list: () => http.get("/negocio/productos/cargas").then((r) => r.data),
  ver: (id) => http.get(`/negocio/productos/cargas/${id}`).then((r) => r.data),

  subir: (archivo) => {
    const cuerpo = new FormData();
    cuerpo.append("archivo", archivo);

    return http
      .post("/negocio/productos/cargas", cuerpo, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  /*
   * LA PLANTILLA, COMO BLOB.
   *
   * No se puede poner la URL en un `<a href>`: la sesión viaja en la cabecera
   * `Authorization`, y el navegador no la manda en una navegación. El botón
   * abría una pestaña con `{"message":"Unauthenticated."}` — comprobado, 401.
   *
   * Se descarga con el cliente que sí lleva el token y se crea una URL local.
   * Es lo mismo que ya hace el panel de administración con el PDF de los
   * comprobantes, por el mismo motivo.
   */
  plantilla: () =>
    http
      .get("/negocio/productos/cargas/plantilla", { responseType: "blob" })
      .then((r) => r.data),
};

/** Los domiciliarios asignados a este negocio, con cuántos pedidos llevan
    encima ahora mismo: es lo que decide a quién se le puede despachar. */
export const domiciliariosDelNegocio = {
  list: () => http.get("/negocio/domiciliarios").then((r) => r.data),

  /*
   * Cómo le fue a cada uno en un periodo: entregas, minutos medios y
   * cumplimiento del plazo. La lista de arriba es una foto del AHORA y no
   * sirve para decidir a quién llamar primero mañana; esta sí.
   */
  rendimiento: (periodo) =>
    http
      .get("/negocio/domiciliarios/rendimiento", {
        params: { from: periodo?.desde, to: periodo?.hasta },
      })
      .then((r) => r.data),
};

/** Lo que los compradores escribieron sobre la tienda. Solo lectura: una
    calificación que el calificado puede borrar no es una calificación. */
export const resenas = {
  list: () => http.get("/negocio/resenas").then((r) => r.data),
};

/* =========================================================================
   CHAT

   LAS MISMAS RUTAS QUE USA EL TELÉFONO, no unas nuevas. El tendero que
   responde desde el computador y el que responde desde el móvil escriben en
   la misma conversación; inventar acá un formato propio dejaría mensajes que
   el teléfono no sabría pintar.

   Por eso estas tres llevan `user_id` en el cuerpo, que es lo contrario de lo
   que hace el resto de este archivo: son rutas antiguas, compartidas con la
   app publicada, y el servidor comprueba que la conversación sea de quien la
   pide (responde 403 si no). No se pueden limpiar sin romper las versiones
   que ya están instaladas.
   ======================================================================== */
export const chats = {
  /** Las conversaciones de quien entró, con su último mensaje. */
  lista: (userId) =>
    http.post("/chats/user-chats", { user_id: userId }).then((r) => r.data),

  /** Una conversación entera: participantes y mensajes. */
  conversacion: (chatId) =>
    http.post("/chats/messages", { chat_id: chatId }).then((r) => r.data),

  enviar: ({ userId, destinatarioId, texto }) =>
    http
      .post("/chats/send-message", {
        user_id: userId,
        recipient_id: destinatarioId,
        content: texto,
      })
      .then((r) => r.data),
};

/* =========================================================================
   PROMOCIONES DE LA TIENDA

   El tendero escribe una frase, elige a qué productos se refiere, hasta
   cuándo vale y cuántas veces se puede usar, y le llega a sus clientes
   afiliados como aviso y como notificación.

   No hay borrador: escribir y enviar son la misma acción, igual que en el
   teléfono. Corregir arregla el aviso guardado pero NO vuelve a sonar.
   ======================================================================== */
/* =========================================================================
   LA CAMPANA Y LA CUENTA

   Los avisos del tendero vivian solo en el telefono: sentado en el mostrador,
   el panel era el unico sitio donde no se enteraba de que entro un pedido.

   Las rutas de seguridad llevan el prefijo `admin/` por como nacieron, pero NO
   son del equipo: no tienen puerta de administrador y cada quien solo toca su
   propia cuenta. Se usan tal cual en vez de duplicarlas.
   ======================================================================== */
export const avisos = {
  lista: () => http.get("/users/notifications").then((r) => r.data),

  leer: (id) =>
    http.put("/users/notifications/read", { notification_id: id }).then((r) => r.data),

  leerTodos: () => http.put("/users/notifications/read-all").then((r) => r.data),
};

export const cuenta = {
  /** Segundo factor y sesiones abiertas de quien entro. */
  seguridad: () => http.get("/admin/security").then((r) => r.data),

  perfil: (body) => http.put("/users/update", body).then((r) => r.data),

  clave: ({ actual, nueva, confirmacion }) =>
    http
      .put("/users/password", {
        current_password: actual,
        password: nueva,
        password_confirmation: confirmacion,
      })
      .then((r) => r.data),

  /*
   * El secreto del segundo factor sale UNA sola vez, aca. No se guarda ni se
   * vuelve a pedir: quien lo lea puede generar los mismos codigos que el
   * telefono.
   */
  iniciarDosFactores: () => http.post("/admin/security/two-factor").then((r) => r.data),

  confirmarDosFactores: (codigo) =>
    http
      .post("/admin/security/two-factor/confirm", { code: codigo })
      .then((r) => r.data),

  apagarDosFactores: (clave) =>
    http
      .delete("/admin/security/two-factor", { data: { password: clave } })
      .then((r) => r.data),

  nuevosCodigos: () =>
    http.post("/admin/security/two-factor/recovery-codes").then((r) => r.data),

  cerrarLasDemas: () =>
    http.delete("/admin/security/sessions/others").then((r) => r.data),
};

/* =========================================================================
   CLIENTES AFILIADOS Y FIADO

   Afiliar es lo que convierte a un comprador cualquiera en cliente de ESTA
   tienda: quien recibe sus promociones y a quien se le puede fiar.

   El negocio NO viaja en el cuerpo: sale de la sesion y de la cabecera
   `X-Negocio`, como el resto del ambito. Las rutas viejas de la app lo reciben
   y lo comprueban, pero mezclar las dos formas en el mismo panel es como se
   cuela manana una pantalla que afilia al negocio equivocado.
   ======================================================================== */
export const clientes = {
  /** Los afiliados y su cupo, mas el panorama de credito de la tienda. */
  lista: () => http.get("/negocio/creditos").then((r) => r.data),

  /*
   * Un 404 aca NO es un error: es el caso normal de un telefono que no tiene
   * cuenta, y la pantalla lo usa para decirlo con palabras. Por eso devuelve
   * null en vez de dejar que reviente.
   */
  buscar: (telefono) =>
    http
      .get("/negocio/clientes/buscar", { params: { telefono } })
      .then((r) => r.data?.cliente ?? null)
      .catch((e) => {
        if (e?.response?.status === 404) return null;
        throw e;
      }),

  afiliar: (userId) => http.post(`/negocio/clientes/${userId}`).then((r) => r.data),
  desafiliar: (userId) => http.delete(`/negocio/clientes/${userId}`).then((r) => r.data),

  /** El cupo que la tienda le fija. Cero lo deja sin credito. */
  cupo: (userId, limite) =>
    http
      .put(`/negocio/creditos/${userId}`, { credit_limit: limite })
      .then((r) => r.data),

  /** Le pago a la tienda: se libera ese valor de su cupo. */
  abonar: (userId, { valor, nota }) =>
    http
      .post(`/negocio/creditos/${userId}/abonos`, { amount: valor, notes: nota })
      .then((r) => r.data),

  movimientos: (userId) =>
    http.get(`/negocio/creditos/${userId}/movimientos`).then((r) => r.data),
};

/* =========================================================================
   LA TORRE DE CONTROL DE LA FLOTA

   Quien reparte, que lleva encima cada uno y cuanta plata carga, en UNA
   llamada. Los datos existen repartidos en tres endpoints, pero esta pantalla
   se refresca sola: con seis domiciliarios serian ocho llamadas por vuelta.
   ======================================================================== */
export const torre = {
  cargar: () => http.get("/negocio/torre").then((r) => r.data),
};

export const promociones = {
  /** Las que ya mandó, más lo que la pantalla necesita para abrirse. */
  lista: () => http.get("/negocio/promociones").then((r) => r.data),

  enviar: (cuerpo) =>
    http.post("/negocio/promociones", cuerpo).then((r) => r.data),

  corregir: (id, cuerpo) =>
    http.put(`/negocio/promociones/${id}`, cuerpo).then((r) => r.data),

  retirar: (id) =>
    http.delete(`/negocio/promociones/${id}`).then((r) => r.data),
};
